import { useEffect } from "react";

/**
 * Lightweight per-page SEO without a runtime dependency.
 *
 * Updates client-side AFTER React mounts. For initial-crawl SEO,
 * Google still uses the static <title> + meta in /public/index.html,
 * plus the static Organization/Logo JSON-LD we ship there. This hook
 * adds per-route polish for users + secondary crawlers that execute JS.
 *
 * Optional `jsonLd` argument: pass an array of structured-data objects
 * to inject as <script type="application/ld+json"> for the current
 * page (FAQPage, Service, BreadcrumbList, etc.). Old per-page scripts
 * are cleared on each navigation so we never accumulate stale ones.
 */
export function useSeo({ title, description, canonical, locale, jsonLd }) {
  // Serialized once per render so the effect deps do a stable deep comparison.
  const jsonLdKey = JSON.stringify(jsonLd || null);
  useEffect(() => {
    if (typeof document === "undefined") return;
    if (title) document.title = title;

    const setMeta = (selector, attr, value) => {
      if (!value) return;
      let el = document.querySelector(selector);
      if (!el) {
        el = document.createElement("meta");
        if (selector.includes('name=')) el.setAttribute("name", selector.match(/name="([^"]+)"/)[1]);
        if (selector.includes('property=')) el.setAttribute("property", selector.match(/property="([^"]+)"/)[1]);
        document.head.appendChild(el);
      }
      el.setAttribute(attr, value);
    };

    if (description) {
      setMeta('meta[name="description"]', "content", description);
      setMeta('meta[property="og:description"]', "content", description);
      setMeta('meta[name="twitter:description"]', "content", description);
    }
    if (title) {
      setMeta('meta[property="og:title"]', "content", title);
      setMeta('meta[name="twitter:title"]', "content", title);
    }
    if (canonical) {
      let link = document.querySelector('link[rel="canonical"]');
      if (!link) {
        link = document.createElement("link");
        link.setAttribute("rel", "canonical");
        document.head.appendChild(link);
      }
      link.setAttribute("href", canonical);
      setMeta('meta[property="og:url"]', "content", canonical);
    }
    if (locale) {
      document.documentElement.lang = locale;
      setMeta('meta[property="og:locale"]', "content", locale === "de" ? "de_DE" : "en_US");
    }

    // Clear previously injected page-specific JSON-LD blocks before adding new ones.
    document
      .querySelectorAll('script[type="application/ld+json"][data-page-jsonld]')
      .forEach((s) => s.remove());

    const parsed = jsonLdKey ? JSON.parse(jsonLdKey) : null;
    const items = Array.isArray(parsed) ? parsed : parsed ? [parsed] : [];
    items.forEach((obj) => {
      if (!obj) return;
      const script = document.createElement("script");
      script.type = "application/ld+json";
      script.setAttribute("data-page-jsonld", "1");
      try {
        script.text = JSON.stringify(obj);
        document.head.appendChild(script);
      } catch (err) {
        console.warn("useSeo: failed to serialize JSON-LD block", err);
      }
    });
  }, [title, description, canonical, locale, jsonLdKey]);
}

/* ----- Helpers to build common schemas ----- */

const SITE = "https://www.joshifusiontech.de";
const LOGO = `${SITE}/jf-logo.png`;

export const organizationSchema = () => ({
  "@context": "https://schema.org",
  "@type": "Organization",
  "@id": `${SITE}/#organization`,
  name: "Joshi FusionTech Media",
  alternateName: "JFT Media",
  url: SITE,
  logo: {
    "@type": "ImageObject",
    "@id": `${SITE}/#logo`,
    url: LOGO,
    contentUrl: LOGO,
    caption: "Joshi FusionTech Media",
    width: 512,
    height: 512,
  },
  image: LOGO,
  email: "info@joshifusiontech.de",
  address: {
    "@type": "PostalAddress",
    addressLocality: "Berlin",
    addressCountry: "DE",
  },
  founder: { "@type": "Person", name: "Rudrik Joshi", jobTitle: "Inhaber & Founder" },
  sameAs: [
    "https://www.linkedin.com/company/joshifusiontech",
    "https://www.instagram.com/joshifusiontech/",
  ],
  contactPoint: [
    {
      "@type": "ContactPoint",
      contactType: "customer support",
      email: "info@joshifusiontech.de",
      availableLanguage: ["German", "English"],
      areaServed: ["DE", "EU"],
    },
  ],
});

export const breadcrumbSchema = (items) => ({
  "@context": "https://schema.org",
  "@type": "BreadcrumbList",
  itemListElement: items.map((it, i) => ({
    "@type": "ListItem",
    position: i + 1,
    name: it.name,
    item: it.url,
  })),
});

export const faqSchema = (items) => ({
  "@context": "https://schema.org",
  "@type": "FAQPage",
  mainEntity: items.map((it) => ({
    "@type": "Question",
    name: it.q,
    acceptedAnswer: { "@type": "Answer", text: it.a },
  })),
});

export const serviceSchema = ({ name, description, url, slug }) => ({
  "@context": "https://schema.org",
  "@type": "Service",
  "@id": `${SITE}/services/${slug}#service`,
  serviceType: name,
  name,
  description,
  url,
  provider: { "@id": `${SITE}/#organization` },
  areaServed: [
    { "@type": "Country", name: "Germany" },
    { "@type": "AdministrativeArea", name: "European Union" },
  ],
});
